"use client";

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from "next-auth/react";
import Link from "next/link";
import Image from "next/image";
import { createDeviceHash } from "@/lib/utils";
import CampaignOverviewMap from "@/components/maps/CampaignOverviewMap";
import { clientLogger } from "@/lib/client-logger";
import { isWhitelabelMode } from "@/lib/whitelabel-config";
import { useMintingMachine } from "./hooks";
import { MintingSection, AssetCard } from "./MintingSection";
import { EligibilityStatus } from "./EligibilityStatus";
import { InlineAuthPanel } from "@/components/auth/InlineAuthPanel";
import type { CheckEligibilityResponse } from "@/lib/claims/eligibility-types";

// Cache in-flight and resolved verify responses by iykRef across component
// instances. React Strict Mode (and some parent re-renders) mount the component
// twice in quick succession. Without caching, the single-use ref is consumed
// twice and the second response (isValidRef=false) clobbers the first.
// Caching the promise lets both mounts resolve from the same network call.
type VerifyResponseData = {
  uid?: string;
  isSimulated?: boolean;
  isValidRef?: boolean;
  alreadyClaimed?: boolean;
  otp?: { code: string; expiresAt: string };
};
const verifyPromises = new Map<string, Promise<VerifyResponseData>>();

// Test-only: clear the verify cache so module-level state doesn't leak
// across tests. Not for production use.
export function __resetVerifyPromiseCacheForTests() {
  verifyPromises.clear();
}

/**
 * Route pattern configuration for navigation links.
 * - 'legacy': Uses /hunt/[slug]/[clue] pattern
 * - 'standard': Uses /hunt/[slug]/clues/[clue-slug] pattern
 * - 'tenant-campaign': Uses /[tenant]/campaigns/[slug]/locations/[clue-slug] pattern
 */
export type RoutePattern = 'legacy' | 'standard' | 'tenant-campaign';

/**
 * Props for the shared ClueClient component.
 * Supports multiple route patterns:
 * - /[tenant]/campaigns/[slug]/locations/[clue-slug] (preferred)
 * - /hunt/[slug]/clues/[clue-slug] (legacy)
 * - /hunt/[slug]/[clue] (legacy)
 */
export interface ClueClientProps {
  clue: {
    id: string;
    title: string;
    description: string;
    address: string;
    latitude?: number;
    longitude?: number;
    radiusMiles?: number;
    hideAddress?: boolean;
    showMap?: boolean;
    slug: string;
    orderIndex: number;
    assetMetadata: Record<string, unknown> | null;
    assetTemplateId?: string;
    atomicTemplateId?: string;
    onChainTemplateId?: string;
    /** NFT template name from atomicTemplate */
    assetName?: string;
    /** NFT image CID from atomicTemplate.immutableData.img */
    assetImage?: string;
    /** NFT video CID from atomicTemplate.immutableData.video */
    assetVideo?: string;
    imageUrl?: string | null;
  };
  campaign: {
    id: string;
    slug: string;
    title: string;
    orgName: string;
    showMap?: boolean;
    chain?: string;
    campaignType?: string;
    authMode?: string;
  };
  tenant: {
    slug: string;
    name: string;
    logoUrl: string | undefined;
    primaryColor: string;
  };
  navigation: {
    prev: { slug: string; title: string };
    next: { slug: string; title: string };
  };
  searchParams: { [key: string]: string | string[] | undefined };
  /**
   * Route pattern to use for navigation links.
   * @default 'standard'
   */
  routePattern?: RoutePattern;
}

export interface VerificationState {
  isLoading: boolean;
  error: string | null;
  data: {
    uid: string;
    isSimulated: boolean;
    isValidRef: boolean;
    alreadyClaimed: boolean;
    otp?: {
      code: string;
      expiresAt: string;
    };
  } | null;
}

export interface NFTOwnershipState {
  isChecking: boolean;
  owns: boolean | null;
  assetId?: string;
  error?: string;
}

/**
 * State for eligibility checking
 */
export interface EligibilityState {
  isLoading: boolean;
  data: CheckEligibilityResponse | null;
  error: string | null;
}

// MintingState is re-exported from hooks for backwards compatibility
export type { MintingState } from "./hooks";

/**
 * Generates the clue navigation URL based on the route pattern.
 */
function getClueUrl(campaignSlug: string, clueSlug: string, pattern: RoutePattern, tenantSlug?: string): string {
  if (pattern === 'legacy') {
    return `/hunt/${campaignSlug}/${clueSlug}`;
  }
  if (pattern === 'tenant-campaign' && tenantSlug) {
    return `/${tenantSlug}/campaigns/${campaignSlug}/locations/${clueSlug}`;
  }
  return `/hunt/${campaignSlug}/clues/${clueSlug}`;
}

/**
 * Generates the back link URL based on the route pattern.
 */
function getBackUrl(campaignSlug: string, tenantSlug: string, pattern: RoutePattern): string {
  if (pattern === 'legacy') {
    return `/${tenantSlug}/clues`;
  }
  if (pattern === 'tenant-campaign') {
    return `/${tenantSlug}/campaigns/${campaignSlug}/locations`;
  }
  return `/hunt/${campaignSlug}/clues`;
}

/**
 * Shared ClueClient component for displaying clue details and handling
 * IYK NFC tap verification, authentication, and NFT claiming.
 *
 * Used by:
 * - /[tenant]/campaigns/[slug]/locations/[location-slug]/page.tsx (preferred)
 *
 * Features:
 * - IYK reference verification via /api/iyk/verify
 * - Session-based authentication with login/signup prompts
 * - NFT ownership checking (server-side via /api/claim-asset)
 * - Prerequisite eligibility checking via /api/clues/[id]/check-eligibility
 * - Minting state machine with polling and retry logic
 * - WAX wallet integration
 * - Configurable navigation URL patterns
 */
export function ClueClient({
  clue,
  campaign,
  tenant,
  navigation,
  searchParams,
  routePattern = 'standard'
}: ClueClientProps) {
  const { data: session, status: sessionStatus } = useSession();

  const [verificationState, setVerificationState] = useState<VerificationState>({
    isLoading: false,
    error: null,
    data: null,
  });

  const [nftOwnership, setNftOwnership] = useState<NFTOwnershipState>({
    isChecking: false,
    owns: null,
  });

  // Eligibility state for prerequisite checking
  const [eligibilityState, setEligibilityState] = useState<EligibilityState>({
    isLoading: false,
    data: null,
    error: null,
  });

  // Helper to safely get search params
  const getSearchParam = useCallback((key: string): string | null => {
    if (!searchParams) return null;
    const value = searchParams[key];
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value.length > 0) return value[0];
    return null;
  }, [searchParams]);

  // Minting state machine hook - handles claim creation, polling, and retry logic
  const {
    state: mintingState,
    startClaim,
    retry: retryMint,
    walletConnected,
  } = useMintingMachine({
    clueId: clue.id,
    campaignId: campaign.id,
    hasAtomicTemplateId: !!clue.atomicTemplateId,
    getOtp: () => {
      const iykRef = getSearchParam('iykRef') || getSearchParam('iykref') || '';
      return sessionStorage.getItem(`iyk_otp_${iykRef}`);
    },
    getIykRef: () => getSearchParam('iykRef') || getSearchParam('iykref') || '',
    getChipId: () => verificationState.data?.uid || '',
    createDeviceHash: () => createDeviceHash(navigator.userAgent, Date.now()),
    onOwnershipChange: (owns, assetId) => {
      setNftOwnership({
        isChecking: false,
        owns,
        assetId,
      });
    },
  });

  // Function to check eligibility
  const checkEligibility = useCallback(async () => {
    if (!session?.user) {
      clientLogger.debug("Skipping eligibility check - no session");
      return;
    }

    // Skip eligibility check for POAP campaigns — prerequisites are WAX/AtomicAssets-specific
    if (campaign.chain === 'POAP') {
      clientLogger.debug("Skipping eligibility check - POAP campaign");
      setEligibilityState({
        isLoading: false,
        data: { eligible: true, hasPrerequisites: false, required: [], owned: [], missing: [], logicType: 'AND' },
        error: null,
      });
      return;
    }

    setEligibilityState({
      isLoading: true,
      data: null,
      error: null,
    });

    try {
      clientLogger.debug("Checking eligibility for clue", { clueId: clue.id });

      const response = await fetch(`/api/clues/${clue.id}/check-eligibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Server will use session for user context
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check eligibility');
      }

      clientLogger.debug("Eligibility check result", {
        clueId: clue.id,
        eligible: result.eligible,
        hasPrerequisites: result.hasPrerequisites,
        required: result.required?.length ?? 0,
        owned: result.owned?.length ?? 0,
        missing: result.missing?.length ?? 0,
      });

      setEligibilityState({
        isLoading: false,
        data: result,
        error: null,
      });
    } catch (err) {
      const error = err as Error;
      clientLogger.error("Error checking eligibility", {
        error: error.message,
        clueId: clue.id,
      });
      setEligibilityState({
        isLoading: false,
        data: null,
        error: error.message || 'Failed to check eligibility',
      });
    }
  }, [clue.id, campaign.chain, session?.user]);

  // Wrapper to check preconditions before starting claim
  const handleMintClick = useCallback(() => {
    if (!session?.user || !verificationState.data?.isValidRef) {
      return;
    }
    // Check eligibility before allowing claim
    if (eligibilityState.data?.hasPrerequisites && !eligibilityState.data?.eligible) {
      clientLogger.warn("Attempted to claim without meeting prerequisites");
      return;
    }
    startClaim();
  }, [session?.user, verificationState.data?.isValidRef, eligibilityState.data, startClaim]);

  // Wrapper for retry
  const handleRetryMint = useCallback(() => {
    retryMint();
  }, [retryMint]);

  // Handle wallet connected inline - reset machine and auto-retry claim
  const handleWalletConnected = useCallback((accountName: string) => {
    clientLogger.info("Wallet connected inline, auto-retrying claim", { accountName });
    walletConnected();
    // Auto-trigger claim now that wallet exists
    startClaim();
  }, [walletConnected, startClaim]);

  // Function to verify IYK reference
  const verifyIYKReference = useCallback(async (iykRef: string, otpCode?: string) => {
    setVerificationState({ isLoading: true, error: null, data: null });

    // Dedupe the network call across Strict Mode remounts: if another mount
    // already kicked off (or completed) a verify for this iykRef, await its
    // promise instead of firing a second request against a single-use token.
    let promise = verifyPromises.get(iykRef);
    if (!promise) {
      promise = (async () => {
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (otpCode) {
          headers['x-iyk-code'] = otpCode;
          clientLogger.debug("Sending OTP with verification request");
        }

        const response = await fetch('/api/iyk/verify', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            iykRef,
            clueId: clue.id,
            campaignId: campaign.id,
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Failed to verify IYK reference');
        }

        // Store OTP in sessionStorage for subsequent requests
        if (result.data?.otp?.code) {
          const otpKey = `iyk_otp_${iykRef}`;
          sessionStorage.setItem(otpKey, result.data.otp.code);
          clientLogger.debug("OTP stored for future requests", {
            otpKey,
            iykRefLength: iykRef.length,
            otpCodePreview: result.data.otp.code.substring(0, 8) + '...',
          });
        }

        return result.data as VerifyResponseData;
      })();
      verifyPromises.set(iykRef, promise);
    }

    try {
      const data = await promise;

      // If we had a stored OTP, this is a re-verification after login — the
      // original tap was valid but the IYK ref is single-use, so trust the OTP.
      const isReVerification = !!otpCode;

      setVerificationState({
        isLoading: false,
        error: null,
        data: {
          uid: data?.uid || iykRef,
          isSimulated: data?.isSimulated || false,
          isValidRef: isReVerification ? true : (data?.isValidRef || false),
          alreadyClaimed: data?.alreadyClaimed ?? false,
          otp: data?.otp,
        },
      });
    } catch (err) {
      // Evict failed promise so a user-initiated retry can try again
      verifyPromises.delete(iykRef);
      const error = err as Error;
      clientLogger.error("Error verifying IYK reference", {
        error: error.message,
        clueId: clue.id
      });
      setVerificationState({
        isLoading: false,
        error: error.message || 'Failed to verify location. Please try again.',
        data: null,
      });
    }
  }, [clue.id, campaign.id]);

  // Tracks the iykRef we've already kicked off verification for, so we never
  // verify the same single-use ref twice within one mount.
  const verifiedRefKickedOff = useRef<string | null>(null);

  // Check for IYK reference on initial load
  useEffect(() => {
    const iykRef = getSearchParam('iykRef') || getSearchParam('iykref');

    // Verify each ref exactly once per mount. This effect re-runs whenever the
    // searchParams prop identity churns on re-render; firing verify again would
    // hit IYK a second time against the single-use ref. With no OTP to
    // force-trust it, that second isValidRef:false response clobbered the first
    // valid result — surfacing as a bogus "this link has been shared" panel for
    // users who were already logged in when they tapped. (Logged-out users were
    // shielded because their post-login re-verify carries the stored OTP.)
    if (iykRef && verifiedRefKickedOff.current === iykRef) return;

    const allKeys = Object.keys(sessionStorage);
    const otpKeys = allKeys.filter(k => k.startsWith('iyk_otp_'));

    clientLogger.debug("IYK verification effect running", {
      hasIykRef: !!iykRef,
      iykRef: iykRef ? iykRef.substring(0, 12) + '...' : 'none',
      iykRefFull: iykRef, // Log full ref for encoding comparison
      expectedOtpKey: iykRef ? `iyk_otp_${iykRef}` : 'n/a',
      allSessionStorageKeys: allKeys,
      otpKeysFound: otpKeys,
      hasReturnUrl: !!sessionStorage.getItem('returnUrl'),
      returnUrl: sessionStorage.getItem('returnUrl'),
    });

    if (iykRef) {
      verifiedRefKickedOff.current = iykRef;
      const otpKey = `iyk_otp_${iykRef}`;
      const storedOtp = sessionStorage.getItem(otpKey);

      clientLogger.debug("Looking up stored OTP", {
        otpKey,
        otpKeyLength: otpKey.length,
        foundOtp: !!storedOtp,
        otpPreview: storedOtp ? storedOtp.substring(0, 8) + '...' : 'none',
        // Check for similar keys (encoding issues)
        similarKeys: otpKeys.filter(k => k.includes(iykRef.substring(0, 8))),
      });

      if (storedOtp) {
        clientLogger.debug("Found stored OTP, using it for re-verification after auth");
        verifyIYKReference(iykRef, storedOtp);
      } else {
        clientLogger.debug("No stored OTP found, first-time verification");
        verifyIYKReference(iykRef);
      }
    }
  }, [getSearchParam, verifyIYKReference]);

  // Check eligibility after IYK verification succeeds and user is logged in
  useEffect(() => {
    if (
      verificationState.data?.isValidRef &&
      session?.user &&
      !eligibilityState.data &&
      !eligibilityState.isLoading &&
      !eligibilityState.error
    ) {
      checkEligibility();
    }
  }, [verificationState.data?.isValidRef, session?.user, eligibilityState.data, eligibilityState.isLoading, eligibilityState.error, checkEligibility]);

  // Note: NFT ownership verification is now handled server-side in /api/claim-asset
  // The server checks the blockchain for ownership before allowing/rejecting claims
  // This allows re-claiming if the NFT was never minted, failed, or was transferred away

  const hasIykRef = !!(getSearchParam('iykRef') || getSearchParam('iykref'));
  const showScanInstructions = hasIykRef && !verificationState.data?.isValidRef;

  // Memoize asset image/video and name for MintingSection
  // Prefer the atomicTemplate data (assetImage/assetVideo CID) over legacy assetMetadata
  const PINATA_GATEWAY_URL = 'https://gateway.pinata.cloud/ipfs';
  const toGatewayUrl = (cid: string) =>
    cid.startsWith('http://') || cid.startsWith('https://') ? cid : `${PINATA_GATEWAY_URL}/${cid}`;
  const assetImage = clue.assetImage
    ? toGatewayUrl(clue.assetImage)
    : (clue.assetMetadata?.image as string | undefined);
  const assetVideo = clue.assetVideo
    ? toGatewayUrl(clue.assetVideo)
    : undefined;
  const assetName = clue.assetName || clue.title;
  const isPoap = campaign.chain === 'POAP';

  // Determine if user is eligible to claim (considering prerequisites)
  const hasPrerequisites = eligibilityState.data?.hasPrerequisites ?? false;
  const isEligible = !hasPrerequisites || eligibilityState.data?.eligible === true;
  const showEligibilityStatus = hasPrerequisites && !eligibilityState.data?.eligible;

  clientLogger.debug("ClueClient render state", {
    sessionStatus,
    hasSession: !!session,
    hasValidRef: verificationState.data?.isValidRef,
    hasIykRef,
    mintingStatus: mintingState.status,
    routePattern,
    hasPrerequisites,
    isEligible,
  });

  const isSingleLocation = campaign.campaignType === 'SINGLE_LOCATION';

  // Server-side "already claimed" check. The initial iyk/verify response
  // already carries this flag for the current session — /api/claims/check
  // only runs when the user signs IN after verify (e.g. Privy bridge
  // completes post-tap). Gating on the unauthenticated->authenticated
  // transition avoids a redundant fetch for users who were already signed in.
  const [claimCheck, setClaimCheck] = useState<{ alreadyClaimed: boolean } | null>(null);
  const prevSessionStatus = useRef(sessionStatus);
  useEffect(() => {
    const transitionedFromUnauth =
      prevSessionStatus.current === 'unauthenticated' && sessionStatus === 'authenticated';
    prevSessionStatus.current = sessionStatus;
    if (!transitionedFromUnauth) return;

    let cancelled = false;
    fetch(`/api/claims/check?clueId=${encodeURIComponent(clue.id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setClaimCheck(data);
      })
      .catch(() => {
        /* non-fatal — iyk/verify's flag is the primary source */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionStatus, clue.id]);
  const alreadyClaimed =
    claimCheck?.alreadyClaimed === true ||
    verificationState.data?.alreadyClaimed === true;

  const calloutContent = (
    <div className="mb-6">
      {alreadyClaimed ? (
        <div className="bg-green-50 rounded-lg border border-green-200 p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-green-800 mb-2">Already Claimed</h3>
          <p className="text-green-700">You&apos;ve already claimed this asset on this device.</p>
        </div>
      ) : verificationState.isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-gray-600">Verifying your location...</p>
          </div>
        </div>
      ) : sessionStatus === 'loading' ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      ) : !session ? (
        // Not signed in. Auth happens inline (no redirect) via the same widget
        // as /auth/signin — Privy email one-time code or wallet (MetaMask),
        // with email + password as a backup. Once authenticated the
        // page re-renders into the claim flow below. Used for both standard and
        // chain-native campaigns.
        <div className="bg-blue-50 rounded-lg border-l-4 border-blue-400 p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-4">
              {verificationState.data?.isValidRef
                ? 'Claim Your Digital Asset'
                : 'Sign In to Participate'}
            </h3>
            <AssetCard
              message={verificationState.data?.isValidRef
                ? "You've verified your location! Sign in or create an account to claim your exclusive digital asset."
                : "Sign in or create an account to participate in this scavenger hunt and claim digital assets!"}
              assetImage={assetImage}
              assetVideo={assetVideo}
              assetTitle={assetName}
              isPoap={isPoap}
            />
            <InlineAuthPanel />
          </div>
        </div>
      ) : verificationState.data?.isValidRef ? (
        // Show minting section first if mint was successful (before ownership check takes over)
        mintingState.status === 'success' ? (
          <MintingSection
            mintingState={mintingState}
            onMint={handleMintClick}
            onRetry={handleRetryMint}
            onWalletConnected={handleWalletConnected}
            assetImage={assetImage}
            assetVideo={assetVideo}
            assetTitle={clue.title}
            primaryColor={isWhitelabelMode() ? tenant.primaryColor : undefined}
            chain={campaign.chain}
          />
        ) : nftOwnership.isChecking ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Checking your digital assets...</p>
            </div>
          </div>
        ) : nftOwnership.owns ? (
          <div className="bg-green-50 rounded-lg border-l-4 border-green-400 p-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-green-800 mb-2">Already Claimed!</h3>
              <p className="text-green-700 mb-4">
                You&apos;ve already claimed this digital asset. Visit the other locations to complete the scavenger hunt!
              </p>
              {nftOwnership.assetId && (
                <p className="text-sm text-green-600">
                  Asset ID: {nftOwnership.assetId}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Show eligibility status when prerequisites exist */}
            {(eligibilityState.isLoading || showEligibilityStatus) && (
              <div className="mb-4">
                <EligibilityStatus
                  eligible={eligibilityState.data?.eligible ?? false}
                  required={eligibilityState.data?.required ?? []}
                  owned={eligibilityState.data?.owned ?? []}
                  missing={eligibilityState.data?.missing ?? []}
                  logicType={eligibilityState.data?.logicType ?? 'AND'}
                  isLoading={eligibilityState.isLoading}
                />
              </div>
            )}
            {/* Only show minting section when eligible */}
            {isEligible && !eligibilityState.isLoading && (
              <MintingSection
                mintingState={mintingState}
                onMint={handleMintClick}
                onRetry={handleRetryMint}
                onWalletConnected={handleWalletConnected}
                assetImage={assetImage}
                assetVideo={assetVideo}
                assetTitle={clue.title}
                primaryColor={isWhitelabelMode() ? tenant.primaryColor : undefined}
                chain={campaign.chain}
              />
            )}
          </>
        )
      ) : showScanInstructions ? (
        <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Verification Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{isSingleLocation
                  ? "This link has either been shared or has expired. Please check in at the event location for a new link to claim your digital asset."
                  : "This link has either been shared or has expired. Please visit the location and tap the scavenger hunt sign for a new link to claim your digital asset."
                }</p>
              </div>
              <div className="mt-4">
                <AssetCard
                  message={isSingleLocation
                    ? "Claim your digital asset by checking in at the event location."
                    : "Visit this location and scan the sign to claim your exclusive digital asset!"
                  }
                  assetImage={assetImage}
                  assetVideo={assetVideo}
                  assetTitle={assetName}
                  isPoap={isPoap}
                />
              </div>
            </div>
          </div>
        </div>
      ) : verificationState.error ? (
        <div className="bg-red-50 rounded-lg border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Verification Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{verificationState.error}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 rounded-lg border-l-4 border-blue-400 p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Claim Your Exclusive Digital Asset</h3>
            <AssetCard
              message={isSingleLocation
                ? "Claim your digital asset by checking in at the event location."
                : "Visit this location and scan the scavenger hunt sign to claim your exclusive digital asset!"
              }
              assetImage={assetImage}
              assetVideo={assetVideo}
              assetTitle={assetName}
              isPoap={isPoap}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Generate URLs based on route pattern
  const backUrl = getBackUrl(campaign.slug, tenant.slug, routePattern);
  const prevUrl = getClueUrl(campaign.slug, navigation.prev.slug, routePattern, tenant.slug);
  const nextUrl = getClueUrl(campaign.slug, navigation.next.slug, routePattern, tenant.slug);

  if (isSingleLocation) {
    return <>{calloutContent}</>;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <Link
                href={backUrl}
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                &#8592; Back to Locations
              </Link>

              {tenant.logoUrl && (
                <Image
                  src={tenant.logoUrl}
                  alt={tenant.name}
                  className="h-8 w-auto"
                  width={120}
                  height={32}
                  unoptimized
                />
              )}
            </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {clue.imageUrl && (
              <div className="relative w-full aspect-video">
                <Image
                  src={clue.imageUrl}
                  alt={clue.title}
                  fill
                  className="object-cover"
                  unoptimized
                />
              </div>
            )}
            <div className="p-8">
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <span
                    className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
                    style={{
                      backgroundColor: tenant.primaryColor ? `${tenant.primaryColor}20` : undefined,
                      color: tenant.primaryColor || undefined
                    }}
                  >
                    Location {clue.orderIndex + 1}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  {clue.title}
                </h1>
                <p className="text-lg text-gray-600 mb-6">
                  {clue.description}
                </p>
                <div className="flex items-center text-gray-500 mb-6">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {clue.hideAddress && clue.radiusMiles
                    ? `Within ${clue.radiusMiles} mile${clue.radiusMiles !== 1 ? 's' : ''} of this area`
                    : clue.address || 'Location not specified'
                  }
                </div>
              </div>

              {/* Callout Content */}
              {calloutContent}
            </div>
          </div>

          {/* Location Map */}
          {clue.showMap !== false && (
            clue.latitude && clue.longitude ? (
              <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h2 className="text-xl font-bold mb-4 text-gray-900">Location Map</h2>
                <CampaignOverviewMap
                  locations={[{
                    id: clue.id,
                    title: clue.title,
                    address: clue.hideAddress ? undefined : clue.address,
                    latitude: clue.latitude,
                    longitude: clue.longitude,
                    radiusMiles: clue.radiusMiles,
                    hideAddress: clue.hideAddress
                  }]}
                  height="300px"
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h2 className="text-xl font-bold mb-4 text-gray-900">Location</h2>
                <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <div className="text-gray-500">
                    <svg className="mx-auto h-8 w-8 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-gray-600 font-medium">Map not available for this location</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Use the address information above to find this location.
                    </p>
                  </div>
                </div>
              </div>
            )
          )}

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <Link
                href={prevUrl}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                &#8592; {navigation.prev.title}
              </Link>
              <Link
                href={nextUrl}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {navigation.next.title} &#8594;
              </Link>
            </div>
        </div>
      </div>
    </div>
  );
}
